import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FAQCategoryData, FAQResponse, FaqService } from '@app/core/api/faq/faq.service';
import { AccordionComponent } from '@app/shared/components/accordion/accordion.component';
import { ToastService } from '@app/shared/components/toast/toast.service';
import { ComboBoxComponent, ComboBoxOption } from '@app/shared/forms/combo-box/combo-box.component';

export interface FaqItem {
    title: string;
    content: string;
    category?: string;
}

@Component({
    selector: 'app-public-faqs',
    standalone: true,
    imports: [AccordionComponent, FormsModule, ComboBoxComponent],
    templateUrl: './public-faqs.component.html',
    styleUrls: ['./public-faqs.component.scss'],
})
export class PublicFaqsComponent implements OnInit {
    faqsService = inject(FaqService);
    toastService = inject(ToastService);

    searchTerm = '';
    selectedCategory = '';
    items: FaqItem[] = [];
    categories: FAQCategoryData[] = [];
    categoryOptions: ComboBoxOption[] = [];

    get filteredItems(): FaqItem[] {
        let filtered = this.items;

        // Filter by category
        if (this.selectedCategory) {
            filtered = filtered.filter((item) => item.category === this.selectedCategory);
        }

        // Filter by search term
        if (this.searchTerm.trim()) {
            const term = this.searchTerm.toLowerCase();
            filtered = filtered.filter(
                (item) => item.title.toLowerCase().includes(term) || item.content.toLowerCase().includes(term),
            );
        }

        return filtered;
    }

    getAllFaqs() {
        this.faqsService.getAllFAQs().subscribe({
            next: (res: FAQResponse) => {
                this.items = res.data.map((faq: any) => ({
                    title: faq.question,
                    content: faq.answer,
                    category: faq.category || 'Uncategorized',
                }));
            },
            error: () => {
                this.toastService.show({
                    title: 'Error',
                    content: 'Failed to load FAQ',
                    duration: 3000,
                    position: 'right',
                    type: 'error',
                });
            },
        });
    }

    getAllCategories() {
        this.faqsService.getAllFAQCategories().subscribe({
            next: (res: any) => {
                this.categories = res.data;
                // Populate combo box options
                this.categoryOptions = [
                    { label: 'All Categories', value: '' }, // Default "All" option
                    ...this.categories.map((cat) => ({
                        label: cat.name,
                        value: cat.name,
                    })),
                ];
            },
            error: () => {
                this.toastService.show({
                    title: 'Error',
                    content: 'Failed to load categories',
                    duration: 3000,
                    position: 'right',
                    type: 'error',
                });
            },
        });
    }

    ngOnInit() {
        this.getAllFaqs();
        this.getAllCategories();
    }

    onCategorySelect(selectedValue: any) {
        this.selectedCategory = selectedValue || '';
    }
}
